import React, { useMemo, useState } from 'react';
import BillingBalance from '../billing/BillingBalance';
import { useNavigate } from 'react-router-dom';
import {
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiPlus,
  FiRefreshCw,
  FiLogIn,
  FiTarget,
  FiX,
} from 'react-icons/fi';
import ScheduleFormModal from './components/ScheduleFormModal';
import ScheduleItem from './components/ScheduleItem';
import SchedulerHeader from './components/SchedulerHeader';
import CompletionRecordModal from './components/CompletionRecordModal';
import DailyReportModal from './components/DailyReportModal';
import UpcomingExamSection from './components/exams/UpcomingExamSection';
import useScheduler from './hooks/useScheduler';
import { createAiPlanDraft } from './services/schedulerAiService';
import { fetchRecentLearningHistory } from './services/schedulerApi';
import { formatMinutes, getTodayKey } from './utils/dateUtils';
import './Scheduler.scss';

const Scheduler = () => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(getTodayKey);
  const [formPlan, setFormPlan] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [actionError, setActionError] = useState('');
  const [completionPlan, setCompletionPlan] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [isSubmittingDay, setIsSubmittingDay] = useState(false);
  const [isGeneratingAi, setIsGeneratingAi] = useState(false);
  const {
    changeStatus,
    dailyReport,
    error,
    isLoading,
    isAuthenticated,
    loadPlans,
    plans,
    removePlan,
    savePlan,
    submitDay,
    studentName,
  } = useScheduler(selectedDate);

  const summary = useMemo(() => {
    const expectedMinutes = plans.reduce((sum, plan) => sum + Number(plan.expectedMinutes || 0), 0);
    const actualMinutes = plans.reduce((sum, plan) => sum + Number(plan.actualMinutes || 0), 0);
    const completedCount = plans.filter((plan) => plan.status === 'completed').length;
    const recordedCount = plans.filter((plan) => (
      plan.status === 'completed'
      && String(plan.actualContent || '').trim()
      && Number(plan.actualMinutes) > 0
      && Number(plan.understanding) > 0
    )).length;
    const completionRate = plans.length ? Math.round((completedCount / plans.length) * 100) : 0;
    return {
      actualMinutes, completedCount, completionRate, expectedMinutes, recordedCount,
    };
  }, [plans]);

  const openNewPlan = (plan = null) => {
    setFormPlan(plan);
    setActionError('');
    setIsFormOpen(true);
  };

  const handleDelete = async (plan) => {
    if (!window.confirm(`${plan.subject} 계획을 삭제하시겠습니까?`)) return;
    setActionError('');
    try {
      await removePlan(plan.sno);
    } catch (deleteError) {
      setActionError(deleteError.message || '계획을 삭제하지 못했습니다.');
    }
  };

  const handleStatusChange = async (plan, status) => {
    setActionError('');
    try {
      await changeStatus(plan.sno, status);
    } catch (statusError) {
      setActionError(statusError.message || '상태를 변경하지 못했습니다.');
    }
  };

  const handleAiPlan = async () => {
    if (!isAuthenticated || isGeneratingAi) return null;
    setIsGeneratingAi(true);
    setActionError('');
    try {
      const recentHistory = await fetchRecentLearningHistory(selectedDate, 7);
      return await createAiPlanDraft(plans, selectedDate, recentHistory.plans);
    } catch (aiError) {
      setActionError(aiError.message || '추천 계획을 만들지 못했습니다.');
      throw aiError;
    } finally {
      setIsGeneratingAi(false);
    }
  };

  const handleSubmitDay = async () => {
    if (dailyReport?.isSubmitted) {
      setReportData(dailyReport);
      return;
    }

    setIsSubmittingDay(true);
    setActionError('');
    try {
      const result = await submitDay();
      setReportData(result);
    } catch (submitError) {
      setActionError(submitError.message || '오늘 학습을 마감하지 못했습니다.');
    } finally {
      setIsSubmittingDay(false);
    }
  };

  const allPlansCompleted = plans.length > 0 && summary.completedCount === plans.length;
  const canSubmitDay = allPlansCompleted && summary.recordedCount === plans.length;

  return (
    <main className="scheduler-page">
      <header className="scheduler-topbar">
        <div>
          <span>개인 학습 계획</span>
          <h1>AI 스케줄러</h1>
        </div>
        <button type="button" className="scheduler-topbar__close" onClick={() => navigate('/menu')} title="닫기" aria-label="서비스 메뉴로 이동">
          <FiX aria-hidden="true" />
        </button>
      </header>

      <div className="scheduler-content">
        <BillingBalance showWalletLink={false} />
        <section className="scheduler-intro">
          <span><FiTarget aria-hidden="true" /> {studentName ? `${studentName}님의 목표` : '오늘의 목표'}</span>
          <h2>계획하고, 기록하고, 매일 성장해요.</h2>
          <p>시간대별 학습 계획과 실제 학습 내용을 한곳에서 관리할 수 있습니다.</p>
        </section>

        <UpcomingExamSection />

        <SchedulerHeader selectedDate={selectedDate} onDateChange={setSelectedDate} />

        <section className="scheduler-summary" aria-label="오늘의 학습 요약">
          <article>
            <FiClock aria-hidden="true" />
            <span>계획 시간</span>
            <strong>{formatMinutes(summary.expectedMinutes)}</strong>
          </article>
          <article>
            <FiRefreshCw aria-hidden="true" />
            <span>실제 시간</span>
            <strong>{formatMinutes(summary.actualMinutes)}</strong>
          </article>
          <article>
            <FiCheckCircle aria-hidden="true" />
            <span>완료</span>
            <strong>{summary.completedCount}/{plans.length}</strong>
          </article>
          <article>
            <FiTarget aria-hidden="true" />
            <span>완료율</span>
            <strong>{summary.completionRate}%</strong>
          </article>
        </section>

        <section className="scheduler-quick-actions">
          <button type="button" onClick={() => openNewPlan()} disabled={!isAuthenticated}>
            <FiPlus aria-hidden="true" />
            <span><strong>학습 계획 추가</strong><small>시간과 공부할 내용을 입력하세요.</small></span>
          </button>
        </section>

        <section className="scheduler-timeline">
          <header>
            <div>
              <h2>시간대별 학습 계획</h2>
            </div>
            <em>{plans.length}</em>
          </header>

          {!isAuthenticated && (
            <div className="scheduler-auth-required">
              <span><FiLogIn aria-hidden="true" /></span>
              <strong>학생 로그인이 필요합니다.</strong>
              <p>로그인하면 날짜별 학습 계획과 완료 기록을 안전하게 저장할 수 있습니다.</p>
              <button type="button" onClick={() => navigate('/login')}>로그인으로 이동</button>
            </div>
          )}
          {isAuthenticated && (error || actionError) && (
            <div className="scheduler-page-error" role="alert">
              <span>{error || actionError}</span>
              {error && <button type="button" onClick={loadPlans}>다시 시도</button>}
            </div>
          )}
          {isAuthenticated && isLoading && <div className="scheduler-loading"><i /><i /><i /></div>}
          {isAuthenticated && !isLoading && !error && plans.length === 0 && (
            <div className="scheduler-empty">
              <FiCalendar aria-hidden="true" />
              <strong>등록된 학습 계획이 없습니다.</strong>
              <span>위의 학습 계획 추가 버튼으로 첫 계획을 만들어 보세요.</span>
            </div>
          )}
          {isAuthenticated && !isLoading && plans.map((plan) => (
            <ScheduleItem
              plan={plan}
              onComplete={setCompletionPlan}
              onDelete={handleDelete}
              onEdit={openNewPlan}
              onStatusChange={handleStatusChange}
              key={plan.sno}
            />
          ))}

          {isAuthenticated && !isLoading && plans.length > 0 && (
            <button
              type="button"
              className={`scheduler-day-submit ${dailyReport?.isSubmitted ? 'is-submitted' : ''}`.trim()}
              onClick={handleSubmitDay}
              disabled={!canSubmitDay || isSubmittingDay}
            >
              <FiCheckCircle aria-hidden="true" />
              <span>
                <strong>{dailyReport?.isSubmitted ? '일일 리포트 보기' : isSubmittingDay ? '마감 중...' : '오늘 학습 마감'}</strong>
                <small>
                  {canSubmitDay
                    ? '완료 기록을 제출하고 결과를 확인하세요.'
                    : allPlansCompleted
                      ? '완료 항목의 학습 기록을 모두 입력해 주세요.'
                      : '모든 학습 계획을 완료하면 활성화됩니다.'}
                </small>
              </span>
            </button>
          )}
        </section>
      </div>

      {isFormOpen && (
        <ScheduleFormModal
          initialPlan={formPlan}
          isGeneratingAi={isGeneratingAi}
          plans={plans}
          selectedDate={selectedDate}
          onAiRecommend={handleAiPlan}
          onClose={() => setIsFormOpen(false)}
          onSave={savePlan}
        />
      )}

      {completionPlan && (
        <CompletionRecordModal
          plan={completionPlan}
          onClose={() => setCompletionPlan(null)}
          onSave={savePlan}
        />
      )}

      {reportData && (
        <DailyReportModal
          plans={plans}
          report={reportData}
          selectedDate={selectedDate}
          onClose={() => setReportData(null)}
        />
      )}

    </main>
  );
};

export default Scheduler;
